import { makeAutoObservable } from "mobx";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { ViewEntityStore } from "../../common/store/types";
import { phonogramService } from "../services/phonogramService";
import { phonogramDisplayId } from "./PhonogramCommonStore";
import { Phonogram } from "../../../api/graphql";
import { interpreterDisplayId } from "../../interpreter/store/InterpreterCommonStore";
import { getMusicalWorkLinkValues } from "../../musicalWorkLink/store/MusicalWorkLinkCommonStore";
import { ColumnWidthInfo } from "../../common/services/crudServices";
import {
  downloadWithAuth,
  fetchFileBlob,
} from "../../../common/utils/downloadWithAuth";
import { CrudRows } from "../../common/types";
import { formatBytes } from "../../../common/utils";
import { interpreterService } from "../../interpreter/services/interpreterService";
import { InitializeParams } from "../../../store/types";

export class PhonogramViewStore implements ViewEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  service = phonogramService;
  phonogramInstance: Phonogram | null = null;
  cardState: Record<string, boolean> = { phonogramInfo: true };
  savedId: string | null = null;
  cancelled = false;
  isDrawerMode = false;
  isModalMode = false;
  callbackParams: CallbackDataParams | null = null;
  idValue: string | undefined;
  trackNameValue: string = "";
  performersService = interpreterService;
  performersSearchValue: string = "";
  performersIdValue: string = "";
  performersSelectedValues: { displayId: string; id: string }[] = [];
  albumNameValue: string = "";
  publicationYearValue: number | undefined = undefined;
  isrcValue: string = "";
  originalProducerValue: string = "";
  audioFileFiles: CrudRows = {};
  mainAudioValue: boolean = false;
  linksColumnWidths: Record<string, number | string> = {};
  linksRows: CrudRows = {};
  notesValue: string = "";
  pdfViewerOpen: boolean = false;
  pdfViewerUrl: string = "";
  pdfViewerTitle: string = "";

  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;
    this.linksColumnWidths = { __platform: 200, __type: 200, url: 200 };

    makeAutoObservable(this);
  }

  destroy() {
    // this object will be unusable
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  toggleCardState(id: string) {
    this.cardState[id] = !this.cardState[id];
  }

  setCardState(id: string, status: boolean) {
    this.cardState[id] = status;
  }

  getCardState(id: string) {
    if (!(id in this.cardState)) {
      this.cardState[id] = true;
    }
    return !!this.cardState[id];
  }

  *loadPhonogramById(id: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.phonogramOne({ id });
    this.phonogramInstance = result.phonogramOne;
    if (this.phonogramInstance) {
      this.loadValues(this.phonogramInstance);
    }
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get displayId(): string {
    if (this.phonogramInstance) {
      return phonogramDisplayId(this.phonogramInstance, this.langStore);
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.PhonogramSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  setContentTop(_contentTop: number) {}

  *returnedData(params: CallbackDataParams): any {
    if (
      (!params.storeId || params.storeId === this.storeId) &&
      params.returnData &&
      !params.returnData.cancelled &&
      params.returnData.resultId
    ) {
      if (params.callerData?.fieldName === "performers") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.interpreterOne({
          id: params.returnData.resultId,
        });
        const interpreter = result.interpreterOne;
        this.performersSelectedValues.push(interpreter);
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  get performersValues() {
    return this.performersSelectedValues.map((x) => {
      return {
        id: x.id,
        displayId: interpreterDisplayId(x, this.langStore),
      };
    });
  }

  get linksVisibleColumns() {
    return [
      {
        name: "__platform",
        title: this.langStore.MusicalWorkLinkPlatformLabel,
        width: this.linksColumnWidths["__platform"],
      },
      {
        name: "__type",
        title: this.langStore.MusicalWorkLinkTypeLabel,
        width: this.linksColumnWidths["__type"],
      },
      {
        name: "url",
        title: this.langStore.MusicalWorkLinkUrlLabel,
        width: this.linksColumnWidths["url"],
      },
    ];
  }

  linksSetColumnWiths(x: ColumnWidthInfo[]) {
    const values: Record<string, number | string> = {};
    for (const item of x) {
      values[item.columnName] = item.width;
    }
    this.linksColumnWidths = values;
  }

  get linksRowsDyn() {
    const entries = Object.entries(this.linksRows).map(([key, value]) => [
      key,
      {
        ...value,
        __platform: this.langStore.displayIdByLang(value.__platform),
        __type: this.langStore.displayIdByLang(value.__type),
      },
    ]);
    return Object.fromEntries(entries);
  }

  *fileDownload(props: any, fieldName: string): any {
    yield downloadWithAuth({
      entityName: "Phonogram",
      fieldName: fieldName,
      instanceId: this.phonogramInstance?.id!,
      fileName: props.name,
    });
  }

  openPdfViewer(url: string, title: string) {
    this.pdfViewerUrl = url;
    this.pdfViewerTitle = title;
    this.pdfViewerOpen = true;
  }

  closePdfViewer() {
    if (this.pdfViewerUrl) {
      window.URL.revokeObjectURL(this.pdfViewerUrl);
    }
    this.pdfViewerOpen = false;
    this.pdfViewerUrl = "";
    this.pdfViewerTitle = "";
  }

  *viewFilePdf(props: any, fieldName: string): any {
    try {
      const blob = yield fetchFileBlob({
        entityName: "Phonogram",
        fieldName: fieldName,
        instanceId: this.phonogramInstance?.id!,
        fileName: props.name,
      });
      if (!blob) return;
      this.openPdfViewer(window.URL.createObjectURL(blob), props.name);
    } catch (error) {
      console.error("Error loading PDF:", error);
    }
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.phonogramInstance = null;
    this.trackNameValue = "";
    this.performersSearchValue = "";
    this.performersIdValue = "";
    this.performersSelectedValues = [];
    this.albumNameValue = "";
    this.publicationYearValue = undefined;
    this.isrcValue = "";
    this.originalProducerValue = "";
    this.audioFileFiles = {};
    this.mainAudioValue = false;
    this.linksRows = {};
    this.notesValue = "";
  }

  close() {
    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
    } else {
      this.rootStore.router.goBack();
    }
  }

  cancelPhonogram() {
    this.cancelled = true;
    this.close();
  }

  get isEditEnabled(): boolean {
    return false;
  }

  editPhonogram() {
    const id = this.phonogramInstance?.id;
    if (!id) return;

    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      this.rootStore.closeModalView();
    }
    this.rootStore.router.goTo(
      "/phonogram/update/" + id,
      undefined,
      undefined,
      undefined,
      this.breadcrumbLabel
    );
  }

  printPhonogram(handlePrint: () => void): void {
    handlePrint();
  }

  *closePhonogram(): any {
    yield this.close();
  }

  loadValues(instance: Phonogram) {
    this.idValue = instance.id;
    this.trackNameValue = instance.trackName;
    this.performersSelectedValues = instance.performers
      ? instance.performers
      : [];
    this.albumNameValue = instance.albumName || "";
    this.publicationYearValue = instance.publicationYear || undefined;
    this.isrcValue = instance.isrc || "";
    this.originalProducerValue = instance.originalProducer || "";
    this.audioFileFiles = instance.audioFile_data
      ? {
          [instance.audioFile_data.name]: {
            name: instance.audioFile_data.name,
            formattedSize: formatBytes(instance.audioFile_data?.size || 0),
            mimetype: instance.audioFile_data?.mimetype,
            id: instance.audioFile_data.name,
            __status__: "original",
          },
        }
      : {};
    this.mainAudioValue = instance.mainAudio;
    this.linksRows = instance.links
      ? Object.assign(
          {},
          ...instance.links.map((x) => {
            const processedRow = getMusicalWorkLinkValues(
              x,
              this.langStore,
              false
            );
            return {
              [`__${x.id}`]: { ...processedRow, __status__: "original" },
            };
          })
        )
      : {};

    this.notesValue = instance.notes || "";
  }

  getState(): InitializeParams {
    return {
      id: this.idValue,
    };
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const id = params?.id;

      if (id) {
        yield this.loadPhonogramById(id);
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
